import styles from './DrawerHeader.module.scss';
import EditableDropdown from '../../../../../common/EditableDropdown/EditableDropdown';
import FyMultiSelect from './FyMultiSelect';
import { UNIT_OPTIONS, useUnits } from '../UnitsContext';

interface DrawerHeaderProps {
    availableFys: number[];
    selectedFys: number[];
    onFysChange: (fys: number[]) => void;
}

/** R1 header shell only — title, Units, FY multiselect, tolerance legend. The "Data Availability"
 *  pill is Release 2 scope and is deliberately not built here. The close button belongs to
 *  SideDrawer, which positions it absolutely over this row's reserved right padding. */
function DrawerHeader({ availableFys, selectedFys, onFysChange }: Readonly<DrawerHeaderProps>) {
    const { unit, setUnit } = useUnits();

    return (
        <div className={styles.DrawerHeader}>
            <h2 className={styles.Title}>Reconciliations</h2>
            <div className={styles.Divider} />
            <div className={styles.UnitsWrapper}>
                <span className={styles.UnitsLabel}>Units ₹:</span>
                <EditableDropdown
                    id="reconciliations-units"
                    value={unit}
                    options={[...UNIT_OPTIONS]}
                    setOutput={(next: string) => setUnit(next)}
                    removeInput
                    wrapperStyle={{ width: 'fit-content' }}
                    inputStyle={{
                        height: '2.25rem',
                        // Fixed rather than content-fit: a plain <input> has no CSS way to track its
                        // value's rendered width, so this is sized to the longest option ("Crore"/
                        // "Lakhs") to guarantee no clipping — wider than the ~78px "INR" reads off the
                        // design screenshot, an approximation.
                        width: '5.5rem',
                        padding: '0 2rem 0 0.75rem',
                        border: '1px solid var(--border-color)',
                    }}
                />
            </div>
            <div className={styles.FyWrapper}>
                <span className={styles.FyLabel}>Financial Year</span>
                <div className={styles.FyControl}>
                    <FyMultiSelect availableFys={availableFys} selectedFys={selectedFys} onChange={onFysChange} />
                </div>
            </div>
            <div className={styles.Legend}>
                <span className={styles.LegendItem}>
                    <span className={`${styles.LegendSwatch} ${styles.SwatchGreen}`} /> Δ&lt;10%
                </span>
                <span className={styles.LegendItem}>
                    <span className={`${styles.LegendSwatch} ${styles.SwatchYellow}`} /> Δ&lt;20%
                </span>
                <span className={styles.LegendItem}>
                    <span className={`${styles.LegendSwatch} ${styles.SwatchRed}`} /> Δ20%+
                </span>
                <span className={styles.LegendItem}>
                    <span className={`${styles.LegendSwatch} ${styles.SwatchNotAvailable}`} /> Benchmark
                </span>
            </div>
        </div>
    );
}

export default DrawerHeader;
